import React, { ReactNode, useState } from 'react';
import { Tabs as AntTabs, TabsProps as AntdTabsProps } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { TabsType } from 'antd/lib/tabs';
import styled from 'styled-components';
import { ThemeDisplayStyleKeyType } from 'styles';
import { Flex, Text } from 'UI';
import { BlueActiveWhiteWrapper, GreyBlueWrapper, STab } from './Tabs.styled';

const { TabPane } = AntTabs;

export type TabsVariant = 'white' | 'whiteActiveBlue' | 'greyBlue' | 'blueActiveWhite' | 'greyActiveBlack';

export interface TabsProps extends AntdTabsProps {
  children?: ReactNode;
  defaultActiveKey?: string;
  type?: TabsType;
  size?: SizeType;
  activeKey?: string;
  onChange?: any;
  centered?: boolean;
  disabled?: boolean;
}

export interface LinkTabsProps extends TabsProps {
  onChange: any;
  activeKey: string | undefined;
}

export interface TabItems {
  key: string;
  label: ReactNode;
  additionalAction?: () => void;
  component: ReactNode;
  'data-cy'?: string;
}

export const Tabs = ({ children, ...props }: TabsProps) => {
  return <AntTabs {...props}>{children}</AntTabs>;
};

export const Tab = TabPane;

export const TabsTitle = styled.div`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.57;
  letter-spacing: normal;
  text-align: center;
  margin: 0 15px 0 15px;
`;

export const AltTabs = ({ onChange, children, activeKey, centered, ...props }: LinkTabsProps) => {
  return (
    <AntTabs {...props} onChange={onChange} activeKey={activeKey} centered={centered}>
      {children}
    </AntTabs>
  );
};

export interface TabDataType {
  title: string;
  key: string;
  component: React.ReactNode;
  disabled?: boolean;
}

interface WideTabsCustomType {
  tabData: TabDataType[];
  defaultActiveKey?: string;
  activeKey?: string;
  onChange?: (type: string) => void;
  variant?: TabsVariant;
}

const TabCustom = ({
  title,
  tabKey,
  setTabState,
  active,
  activeKey,
  onChange,
  disabled = false,
  variant = 'white',
}: {
  title: string;
  tabKey: string;
  setTabState: (s: string) => void;
  active: boolean;
  activeKey?: string;
  onChange?: (tabKey: string) => void;
  disabled?: boolean;
  variant: TabsVariant;
}) => {
  const getDisplayStyle = (): ThemeDisplayStyleKeyType | ThemeDisplayStyleKeyType[] | false => {
    if (disabled) {
      return 'disabled';
    }

    switch (variant) {
      case 'whiteActiveBlue':
      case 'greyBlue':
      case 'greyActiveBlack':
        return active ? 'white' : 'default';
      case 'white':
      case 'blueActiveWhite':
      default:
        return active ? 'link' : 'default';
    }
  };

  const getTextType = () => {
    switch (variant) {
      case 'greyBlue':
        return 'h6';
      case 'greyActiveBlack':
      case 'whiteActiveBlue':
      case 'blueActiveWhite':
      case 'white':
      default:
        return 'text';
    }
  };

  return (
    <STab
      onClick={() => {
        if (disabled) {
          return;
        }
        !activeKey && setTabState(tabKey);
        onChange?.(tabKey);
      }}
      active={active}
      disabled={disabled}
      variant={variant}
    >
      <Text type={getTextType()} displayStyle={getDisplayStyle()} disableGutter>
        {title}
      </Text>
    </STab>
  );
};

export const WideTabs = ({ tabData, defaultActiveKey, activeKey, onChange, variant = 'white' }: WideTabsCustomType) => {
  const [tabState, setTabState] = useState<string>(activeKey ?? defaultActiveKey ?? tabData[0]?.key);
  const currentData = tabData.find((td) => td.key === (activeKey ?? tabState))?.component;

  if (tabData.length === 0) {
    return <></>;
  }
  const renderTabs = () => (
    <Flex gap={['greyBlue', 'blueActiveWhite', 'greyActiveBlack'].includes(variant) ? 15 : 0}>
      {tabData.map((td) => (
        <TabCustom
          key={td.key}
          title={td.title}
          tabKey={td.key}
          setTabState={setTabState}
          active={td.key === (activeKey ?? tabState)}
          activeKey={activeKey}
          onChange={onChange}
          disabled={td.disabled}
          variant={variant}
        />
      ))}
    </Flex>
  );

  const renderTabsWrapper = () => {
    switch (variant) {
      case 'greyBlue':
        return <GreyBlueWrapper>{renderTabs()}</GreyBlueWrapper>;
      case 'blueActiveWhite':
        return <BlueActiveWhiteWrapper>{renderTabs()}</BlueActiveWhiteWrapper>;
      case 'whiteActiveBlue':
      case 'white':
      default:
        return renderTabs();
    }
  };

  return (
    <Flex direction={'column'} gap={16}>
      {renderTabsWrapper()}
      <>{currentData}</>
    </Flex>
  );
};
